import { DCEvent } from "@/events/eventsDomain";
import { ExcelCellData, ExcelConstraint } from "@/excel/excelDomain";
import { PreppedI18nState } from "@/i18n/i18nDomain";
import { isTMessage, isTMessageParam, TMessage, TMessageParam } from "@/i18n/tt";
import { useDispatch, useI18n, useTranslation } from "@/stores/RootStoreContext";
import { toggleElement } from "@/utils/arrays";
import { classNames } from "@/utils/classNames";
import { stopPropagation } from "@/utils/events";
import { orderlessFilter } from "@/utils/filter";
import { handleEnter } from "@/utils/keyboardEvents";
import { logDebug } from "@/utils/log";
import { get, isArray, noop, take } from "lodash";
import { observer } from "mobx-react";
import {
  ComponentPropsWithoutRef,
  CSSProperties,
  Fragment,
  ReactElement,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Box, VBox } from "./Box";
import {
  Button,
  ButtonProps,
  IconButton,
  IconButtonProps,
  LinkButton,
  LinkButtonProps,
} from "./Button";
import { Checkbox, CheckboxProps } from "./Checkbox";
import {
  EditableDataTableContext,
  EditableDataTableHandlerResult,
  EditableDataTableState,
  groupRows,
  handleBlur,
  handleClick,
  handleFocus,
  handleFocusCell,
  handleKeyDown,
  handlePopoverClose,
} from "./dataTableDomain";
import { DataTableSort, isCellReadonly, SortDirection, sortTableData } from "./dataTableHelpers";
import { DragAndDrop, DragAndDropScope, useDraggableRow } from "./dragAndDrop";
import { DateInputView, MDateInput, MDateInputProps } from "./DateInput";
import { DropdownMenu, DropdownMenuItems, DropdownMenuProps } from "./Dropdown";
import { Icon, IconProps } from "./Icon";
import { InfoButton, InfoButtonProps } from "./InfoButton";
import { MSelect, MSelectProps, MSelectView, Select, SelectProps, SelectView } from "./Select";
import { Table, TableProps } from "./Table";
import tableStyles from "./Table.module.css";
import { Tag, TagProps } from "./Tag";
import { MTextArea, MTextAreaProps } from "./TextArea";
import { MTextInput, MTextInputProps, TextInputView } from "./TextInput";
import { useSize } from "./useSize";
import { BgColor, bgColorClassName } from "./colors";

export interface TableContext {
  shouldSkipCell?: { (rowIndex: number, columnIndex: number): boolean };
  getRowSpan?: { (rowIndex: number, columnIndex: number): number | undefined };
}

/* eslint-disable @typescript-eslint/no-explicit-any */
export type TableColumnValue =
  | ReactNode
  | TMessage
  | TMessageParam
  | [type: "checkbox", CheckboxProps]
  | [type: "icon", IconProps]
  | [type: "icon-button", IconButtonProps]
  | [type: "info", InfoButtonProps]
  | [type: "link", LinkButtonProps]
  | [type: "mdate-input", MDateInputProps, options?: { onCommit?: () => void }]
  | [
      type: "mtext-input",
      MTextInputProps,
      options?: {
        onCommit?: () => void;
        displayValue?: () => TMessage | TMessageParam | string | undefined;
      },
    ]
  | [type: "mtextarea", MTextAreaProps, options?: { onCommit?: () => void }]
  | [type: "mselect", MSelectProps<any>]
  | [type: "select", SelectProps<any>]
  | [type: "tag", TagProps];

export function mselect<T>(props: MSelectProps<T>): TableColumnValue {
  return ["mselect", props];
}

export interface TableColumn<T> {
  name: string;
  label: string | TMessage | TMessageParam;
  labelElement?: ReactNode;
  width?: "md" | "lg" | string;
  editableWidth?: "md" | "lg";
  align?: "left" | "center" | "right";
  getValue?: (row: T, index: number, i18n: PreppedI18nState) => TableColumnValue;
  getFooterValue?: (
    i18n: PreppedI18nState,
  ) => ReactNode | TMessage | TMessageParam | ["dropdown", DropdownMenuProps];
  getRowGroupValue?: (rows: T[], i18n: PreppedI18nState) => TableColumnValue;
  getSecondaryFooterValue?: (i18n: PreppedI18nState) => ReactNode | TMessage | TMessageParam;
  getSortValue?: (
    row: T,
    index: number,
    i18n: PreppedI18nState,
  ) => TMessage | TMessageParam | string | number | boolean | Date | null | undefined;
  getFilterValue?: (
    row: T,
    index: number,
    i18n: PreppedI18nState,
  ) => TMessage | TMessageParam | string | number | undefined;
  getClassName?: (row: T, index: number) => string | undefined;
  isReadonly?: ((row: T, index: number) => boolean | undefined) | boolean;
  notSorted?: boolean;
  stopClick?: boolean;
  tooltipLabel?: string;
  compact?: boolean;
  exportToExcelLabel?: string | TMessage | TMessageParam;
  exportToExcel?: (
    row: T,
    index: {
      col: number;
      row: number;
      /** Get the column name for the given column index, e.g. 0->A and 1->B */
      name: (col: number) => string;
      /** Get the cell address for the given column and row indices, e.g. 0,0->A1 and 1,1->B2 */
      addr: (col: number, row: number) => string;
    },
    i18n: PreppedI18nState,
  ) => ExcelCellData;
  excelColumnConstraints?: (i18n: PreppedI18nState) => ExcelConstraint[];
  importFromExcel?: (
    row: T,
    data: ExcelCellData,
    { isNewRow, i18n }: { isNewRow?: boolean; i18n: PreppedI18nState },
  ) => void;
  children?: TableColumn<T>[];
}

export type RowProps = { className?: string; style?: { borderTop?: string }; selected?: boolean };

export type RowAction =
  | [type: "button", ButtonProps]
  | [
      type: "context-menu",
      items: DropdownMenuItems,
      buttonProps?: Partial<Omit<IconButtonProps, "type" | "icon" | "tooltip">>,
    ]
  | [type: "icon", IconButtonProps]
  | [type: "link", LinkButtonProps]
  | [type: "react-element", ReactElement];

export type EditableDataTableRef = {
  focusCell: (cell: { rowId: string; colName: string }) => void;
};

export type DataTableDragAndDrop<T> = DragAndDrop<T>;

export interface DataTableProps<T> extends TableProps {
  tableColumns: TableColumn<T>[];
  tableData: T[];
  onRowClick?: ((row: T) => void) | ((row: T) => DCEvent[]);
  rowFilter?: (row: T) => boolean;
  rowProps?: (row: T) => RowProps | undefined;
  getId: (row: T) => string | number;
  getRowGroupId?: (row: T) => string | undefined;
  context?: TableContext;
  withFooterRow?: boolean;
  rowActions?: (row: T) => RowAction[];
  initialSortBy?: string;
  initialSortDirection?: SortDirection;
  sort?: DataTableSort | null;
  onSortChange?: ((sort: DataTableSort) => void) | ((sort: DataTableSort) => DCEvent[]);
  className?: string;
  headerLinkClassName?: string;
  filter?: string;
  initNewRow?: () => T;
  canClickRow?: (row: T) => boolean;
  dragAndDrop?: DataTableDragAndDrop<T>;
  v2?: boolean;
  setEditableRef?: (ref: EditableDataTableRef | null) => void;
  withoutHeaders?: boolean;
  stickyHeader?: boolean;
}

const headerBg = <T,>(variant: DataTableProps<T>["variant"]): BgColor => {
  switch (variant) {
    case "EDITABLE":
    case "EDITABLE_DENSE":
    case "IN_CARD":
    case "IN_CARD_DENSE":
    case "IN_CARD_LARGE":
      return "default";
    default:
      return "base";
  }
};

const defaultFilterValue = <T,>(
  col: TableColumn<T>,
  row: T,
  i: number,
  i18n: PreppedI18nState,
): ReactNode | TMessage | TMessageParam | string | number | undefined => {
  const val = col.getValue?.(row, i, i18n);
  switch (true) {
    case Array.isArray(val):
      switch (val[0]) {
        case "select":
          return val[1].labelFn(val[1].value, i18n);
        case "mselect":
          return (val[1].labelFn ?? val[1].model.getLabel)?.(val[1].model.value, i18n);
        case "mdate-input":
        case "mtext-input":
          return val[1].model.inputValue;
        default:
          return undefined;
      }
    default:
      return val;
  }
};

interface DraggableRowProps<T> extends ComponentPropsWithoutRef<"tr"> {
  row: T;
  rowId: string;
  dragAndDrop: DataTableDragAndDrop<T>;
}

const DraggableRow = observer(function DraggableRow<T>({
  row,
  rowId,
  dragAndDrop,
  children,
  style,
  ...trProps
}: DraggableRowProps<T>): ReactElement {
  const i18n = useI18n();
  const { setRefs, listeners, isDragging, isOver, draggedRow, isDropTarget, transform } =
    useDraggableRow(row, rowId, dragAndDrop);
  return (
    <tr
      ref={setRefs}
      {...trProps}
      {...listeners}
      style={{
        ...style,
        position: "relative",
        transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
        zIndex: transform && isDragging ? 1 : undefined,
        opacity: isDragging ? 0.5 : undefined,
      }}
    >
      {children}
      {isOver && isDropTarget && draggedRow !== undefined && (
        <td className={tableStyles.dropOverlay} aria-hidden>
          {dragAndDrop.renderDropOverlay?.(draggedRow, row, i18n)}
        </td>
      )}
    </tr>
  );
});

export const DataTable = observer(function DataTable<T>({
  context,
  getId,
  getRowGroupId,
  onRowClick,
  rowFilter,
  tableColumns,
  tableData,
  initialSortBy,
  initialSortDirection,
  sort,
  onSortChange,
  headerLinkClassName,
  filter,
  dragAndDrop,
  v2,
  setEditableRef,
  ...props
}: DataTableProps<T>): ReactElement {
  const { tf } = useTranslation();
  const i18n = useI18n();
  const dispatch = useDispatch();
  const [internalSortedBy, setSortedBy] = useState<string | null>(initialSortBy ?? null);
  const [internalSortDirection, setSortDirection] = useState<SortDirection>(
    initialSortDirection ?? "desc",
  );
  const sortedBy = sort === undefined ? internalSortedBy : (sort?.by ?? null);
  const sortDirection = sort === undefined ? internalSortDirection : (sort?.direction ?? "desc");
  const [collapsedColumns, setCollapsedColumns] = useState<string[]>([]);
  const [collapsedRows, setCollapsedRows] = useState<string[]>([]);
  const [editableState, setEditableState] = useState<EditableDataTableState>({});
  const isEditable = v2 && (props.variant === "EDITABLE" || props.variant === "EDITABLE_DENSE");
  const [tbodyEl, setTbodyEl] = useState<HTMLTableSectionElement | null>(null);
  const { setRef: setTheadEl, size: theadSize } = useSize({ measureParent: false });

  const isCollapsed = (col: TableColumn<T>) => collapsedColumns.includes(col.name);
  const isGrouped = tableColumns.some((x) => x.children?.length);
  const groupedColumns = useMemo(
    () =>
      isGrouped
        ? tableColumns.flatMap((x) =>
            collapsedColumns.includes(x.name) ? take(x.children, 1) : (x.children ?? []),
          )
        : tableColumns,
    [isGrouped, tableColumns, collapsedColumns],
  );

  const applyResult = useCallback((result: EditableDataTableHandlerResult | undefined) => {
    if (!result) return;
    logDebug(
      "DataTable::applyResult",
      result?.state,
      result?.effects?.map(([f]) => f.name),
    );
    if (result.state !== undefined) setEditableState(result.state);
    if (result.effects?.length) {
      result.effects?.forEach(([fn, ...args]) => applyResult(fn(...args)));
    }
  }, []);

  const filteredData = rowFilter
    ? tableData.filter(rowFilter)
    : filter
      ? orderlessFilter(
          tableData,
          (row, i) =>
            tableColumns.map((c) => {
              const v = c.getFilterValue
                ? c.getFilterValue(row, i, i18n)
                : defaultFilterValue(c, row, i, i18n);
              switch (true) {
                case isTMessage(v) || isTMessageParam(v):
                  return tf(v);
                case typeof v === "number" || typeof v === "string":
                  return String(v);
                default:
                  return undefined;
              }
            }),
          filter,
        )
      : tableData;

  const sortedData = sortTableData(
    groupedColumns,
    filteredData,
    sortedBy ? { by: sortedBy, direction: sortDirection } : null,
    i18n,
  );

  const editableContext = useMemo((): EditableDataTableContext => {
    const getCellData = ({
      rowId,
      colName,
    }: {
      rowId: string;
      colName: string;
    }): { row: T; rowIdx: number; col: TableColumn<T>; colIdx: number } => {
      const rowIdx = sortedData.findIndex((r) => String(getId(r)) === rowId);
      const colIdx = groupedColumns.findIndex((c) => c.name === colName);
      return {
        row: sortedData[rowIdx],
        rowIdx: rowIdx,
        col: groupedColumns[colIdx],
        colIdx: colIdx,
      };
    };

    return {
      container: tbodyEl,
      findCell: (cell, deltaX, deltaY) => {
        const { rowIdx, colIdx } = getCellData(cell);
        const newRowIdx = rowIdx + deltaY;
        const newColIdx = colIdx + deltaX;
        if (newRowIdx < 0 || newRowIdx >= sortedData.length) return undefined;
        if (newColIdx < 0 || newColIdx >= groupedColumns.length) return undefined;
        return {
          rowId: String(getId(sortedData[newRowIdx])),
          colName: groupedColumns[newColIdx].name,
        };
      },
      getValue: (cell) => {
        const { col, row, rowIdx } = getCellData(cell);
        return col.getValue!(row, rowIdx, i18n);
      },
      isReadonly: (cell) => {
        const { col, row, rowIdx } = getCellData(cell);
        return isCellReadonly(col, row, rowIdx) ?? false;
      },
    };
  }, [sortedData, groupedColumns, getId, i18n, tbodyEl]);

  const renderFooterValue = useCallback(
    (val: ReturnType<NonNullable<TableColumn<T>["getFooterValue"]>>) => {
      switch (true) {
        case isTMessage(val) || isTMessageParam(val):
          return i18n.tf(val);
        case Array.isArray(val):
          switch (val[0]) {
            case "dropdown":
              return <DropdownMenu {...val[1]} />;
            default:
              throw new Error("DataTable: unsupported footer value type" + val[0]);
          }
        default:
          return val;
      }
    },
    [i18n],
  );

  const renderHeaderCell = (col: TableColumn<T>) => {
    return (
      <th
        key={String(col.name)}
        style={{
          width: col.compact ? "1%" : col.width,
        }}
        align={col.align || "right"}
        className={
          col.align === "left" ? "text-left" : col.align === "center" ? "text-center" : "text-right"
        }
        colSpan={isCollapsed(col) ? 1 : col.children?.length}
      >
        {col.children && col.children.length > 1 && tf(col.label) ? (
          <LinkButton
            icon={isCollapsed(col) ? "plus-square" : "minus-square"}
            size={"sm"}
            color={"inherit"}
            underline={false}
            onClick={() => setCollapsedColumns(toggleElement(collapsedColumns, col.name))}
            label={tf(col.label)}
            aria-label={col.tooltipLabel}
            data-tooltip={col.tooltipLabel}
            className={classNames(headerLinkClassName, "whitespace-nowrap")}
          />
        ) : (col.children && col.children.length <= 1) || col.notSorted ? (
          (col.labelElement ?? <span className={"text-sm"}>{tf(col.label)}</span>)
        ) : tf(col.label) ? (
          <LinkButton
            icon={
              sortedBy === col.name
                ? sortDirection === "asc"
                  ? "arrow-up"
                  : "arrow-down"
                : undefined
            }
            size={"sm"}
            color={"inherit"}
            underline={false}
            onClick={() => handleSortClick(col.name)}
            label={tf(col.label)}
            aria-label={col.tooltipLabel}
            data-tooltip={col.tooltipLabel}
            className={classNames(headerLinkClassName, "whitespace-nowrap")}
          />
        ) : null}
      </th>
    );
  };

  const handleSortClick = useCallback(
    (name: string) => {
      const next: DataTableSort = {
        by: name,
        direction: sortDirection === "desc" ? "asc" : "desc",
      };
      setSortDirection(next.direction);
      setSortedBy(next.by);
      const result = onSortChange?.(next);
      if (isArray(result)) {
        dispatch(result);
      }
    },
    [sortDirection, onSortChange, dispatch],
  );

  const latest = useRef({ onRowClick, editableContext, editableState });
  latest.current = { onRowClick, editableContext, editableState };

  const handleRowClick = useCallback(
    (row: T) => {
      const result = latest.current.onRowClick?.(row);
      if (isArray(result)) {
        dispatch(result);
      }
    },
    [dispatch],
  );

  const handlePopoverCloseInCell = useCallback(
    () =>
      applyResult(handlePopoverClose(latest.current.editableContext, latest.current.editableState)),
    [applyResult],
  );

  const editableRef = useMemo<EditableDataTableRef>(() => {
    return {
      focusCell: (cell) => {
        applyResult(handleFocusCell(editableContext, cell));
      },
    };
  }, [editableContext, applyResult]);

  useEffect(() => {
    setEditableRef?.(editableRef);
    return () => {
      setEditableRef?.(null);
    };
  }, [editableRef, setEditableRef]);

  const renderRow = (row: T, rowIndex: number, isInRowGroup = false) => {
    const rowId = String(getId(row));
    const isSelectedRow = editableState.selectedCell?.rowId === rowId;
    return (
      <DataTableRow
        key={getId(row)}
        row={row}
        rowIndex={rowIndex}
        rowId={rowId}
        columns={groupedColumns}
        i18n={i18n}
        variant={props.variant}
        isEditable={!!isEditable}
        isInRowGroup={isInRowGroup}
        context={context}
        rowProps={props.rowProps}
        rowActions={props.rowActions}
        canClickRow={props.canClickRow}
        hasRowClick={!!onRowClick}
        onRowClick={handleRowClick}
        onPopoverClose={handlePopoverCloseInCell}
        dragAndDrop={dragAndDrop}
        selectedColName={isSelectedRow ? editableState.selectedCell?.colName : undefined}
        editorModel={isSelectedRow ? editableState.editorModel : undefined}
      />
    );
  };

  const table = (
    <Table hoverable={!!onRowClick} {...props}>
      {!props.withoutHeaders && (
        <thead
          ref={props.stickyHeader ? setTheadEl : undefined}
          style={
            {
              "--scroll-padding-height": `${theadSize.height}px`,
            } as CSSProperties
          }
          className={
            props.stickyHeader
              ? classNames(
                  "sticky",
                  "top-0",
                  "z-2",
                  "scroll-pt-(--scroll-padding-height)",
                  bgColorClassName(headerBg(props.variant)),
                )
              : undefined
          }
        >
          <tr>{tableColumns.map(renderHeaderCell)}</tr>
          {isGrouped && <tr>{groupedColumns.map(renderHeaderCell)}</tr>}
        </thead>
      )}
      <tbody
        ref={setTbodyEl}
        onFocusCapture={
          !isEditable ? undefined : (event) => applyResult(handleFocus(editableState, event))
        }
        onBlurCapture={
          !isEditable
            ? undefined
            : (event) => applyResult(handleBlur(editableContext, editableState, event))
        }
        onClickCapture={
          !isEditable ? undefined : () => applyResult(handleClick(editableContext, editableState))
        }
        onKeyDownCapture={
          !isEditable
            ? undefined
            : (event) => applyResult(handleKeyDown(editableContext, editableState, event))
        }
      >
        {groupRows(sortedData, isEditable || dragAndDrop ? undefined : getRowGroupId).map(
          (entry) =>
            entry.type === "row" ? (
              renderRow(entry.row, entry.index)
            ) : (
              <Fragment key={entry.groupId}>
                <DataTableRowGroupHeader
                  rows={entry.rows.map((indexed) => indexed.row)}
                  columns={groupedColumns}
                  i18n={i18n}
                  variant={props.variant}
                  hasRowActions={!!props.rowActions}
                  isCollapsed={collapsedRows.includes(entry.groupId)}
                  onToggle={() => setCollapsedRows(toggleElement(collapsedRows, entry.groupId))}
                />
                {!collapsedRows.includes(entry.groupId) &&
                  entry.rows.map((indexed) => renderRow(indexed.row, indexed.index, true))}
              </Fragment>
            ),
        )}
      </tbody>
      {props.withFooterRow && (
        <tfoot>
          <tr>
            {groupedColumns.map((col, i) =>
              context?.shouldSkipCell?.(sortedData.length, i) ? null : (
                <td
                  key={col.name}
                  align={col.align || "right"}
                  className={"font-bold"}
                  style={{
                    padding: col.compact ? 0 : undefined,
                  }}
                >
                  {renderFooterValue(col.getFooterValue?.(i18n))}
                </td>
              ),
            )}
            {props.rowActions && <td />}
          </tr>
          {groupedColumns.some((col) => col.getSecondaryFooterValue !== undefined) && (
            <tr>
              {groupedColumns.map((col, i) =>
                context?.shouldSkipCell?.(sortedData.length, i) ? null : (
                  <td
                    key={col.name}
                    align={col.align || "right"}
                    className={classNames("font-bold text-sm")}
                    style={{
                      padding: col.compact ? 0 : undefined,
                    }}
                  >
                    {renderFooterValue(col.getSecondaryFooterValue?.(i18n))}
                  </td>
                ),
              )}
            </tr>
          )}
        </tfoot>
      )}
    </Table>
  );

  return dragAndDrop ? (
    <DragAndDropScope dragAndDrop={dragAndDrop}>{table}</DragAndDropScope>
  ) : (
    table
  );
});

type DataTableRowProps<T> = {
  row: T;
  rowIndex: number;
  rowId: string;
  columns: TableColumn<T>[];
  i18n: PreppedI18nState;
  variant: TableProps["variant"];
  isEditable: boolean;
  isInRowGroup?: boolean;
  context?: TableContext;
  rowProps?: (row: T) => RowProps | undefined;
  rowActions?: (row: T) => RowAction[];
  canClickRow?: (row: T) => boolean;
  hasRowClick: boolean;
  onRowClick: (row: T) => void;
  onPopoverClose: () => void;
  dragAndDrop?: DataTableDragAndDrop<T>;
  selectedColName?: string;
  editorModel?: TableColumnValue;
};

const DataTableRow = observer(function DataTableRow<T>({
  row,
  rowIndex,
  rowId,
  columns,
  i18n,
  variant,
  isEditable,
  isInRowGroup,
  context,
  rowProps: getRowProps,
  rowActions,
  canClickRow,
  hasRowClick,
  onRowClick,
  onPopoverClose,
  dragAndDrop,
  selectedColName,
  editorModel,
}: DataTableRowProps<T>): ReactElement {
  const isClickable = hasRowClick && (!canClickRow || canClickRow(row));
  const rowProps = getRowProps?.(row);
  const handleClick = isClickable ? () => onRowClick(row) : undefined;
  const trProps: ComponentPropsWithoutRef<"tr"> & { "data-row-id": string } = {
    "data-row-id": rowId,
    tabIndex: isClickable ? 0 : -1,
    onKeyDown: handleClick ? handleEnter(handleClick) : undefined,
    className: classNames(
      rowProps?.className,
      !isClickable && tableStyles.notClickable,
      isInRowGroup && tableStyles.inRowGroup,
    ),
    style: rowProps?.style,
  };
  const cells = (
    <>
      {columns.map((col, columnIndex) => {
        if (context?.shouldSkipCell?.(rowIndex, columnIndex)) return null;
        const value = col.getValue?.(row, rowIndex, i18n);
        const isReadonly = isCellReadonly(col, row, rowIndex);
        const isEditing = selectedColName === col.name && !!editorModel;
        return (
          <td
            onClick={col.stopClick ? undefined : handleClick}
            style={colStyles(col)}
            key={String(col.name)}
            align={col.align || "right"}
            rowSpan={context?.getRowSpan?.(rowIndex, columnIndex)}
            className={classNames(
              isReadonly && tableStyles.readonly,
              rowProps?.selected && tableStyles.selected,
              col.getClassName?.(row, rowIndex),
            )}
            data-width={col.width}
            {...(isEditable && {
              tabIndex: Array.isArray(value) && value[0] !== "icon" ? -1 : 0,
              "data-row-id": rowId,
              "data-col-name": col.name,
              "data-error": hasError(value),
            })}
          >
            {col.getValue
              ? renderCellValue({
                  value: isEditing ? editorModel : value,
                  isEditing,
                  isEditable,
                  isReadonly,
                  align: col.align,
                  variant,
                  tf: i18n.tf,
                  onPopoverClose,
                })
              : get(row, col.name)}
          </td>
        );
      })}
      {rowActions && (
        <td className={classNames("w-0", rowProps?.selected && tableStyles.selected)}>
          <Box alignItems={"center"} justifyContent={"end"}>
            {rowActions(row).map(renderRowAction)}
          </Box>
        </td>
      )}
    </>
  );
  return dragAndDrop ? (
    <DraggableRow row={row} rowId={rowId} dragAndDrop={dragAndDrop} {...trProps}>
      {cells}
    </DraggableRow>
  ) : (
    <tr {...trProps}>{cells}</tr>
  );
});

type DataTableRowGroupHeaderProps<T> = {
  rows: T[];
  columns: TableColumn<T>[];
  i18n: PreppedI18nState;
  variant: TableProps["variant"];
  hasRowActions: boolean;
  isCollapsed: boolean;
  onToggle: () => void;
};

const DataTableRowGroupHeader = observer(function DataTableRowGroupHeader<T>({
  rows,
  columns,
  i18n,
  variant,
  hasRowActions,
  isCollapsed,
  onToggle,
}: DataTableRowGroupHeaderProps<T>): ReactElement {
  return (
    <tr
      tabIndex={0}
      aria-expanded={!isCollapsed}
      onClick={onToggle}
      onKeyDown={handleEnter(onToggle)}
    >
      {columns.map((col, columnIndex) => {
        const value =
          col.getRowGroupValue &&
          renderCellValue({
            value: col.getRowGroupValue(rows, i18n),
            isEditing: false,
            isEditable: false,
            isReadonly: false,
            align: col.align,
            variant,
            tf: i18n.tf,
            onPopoverClose: noop,
          });
        return (
          <td key={String(col.name)} style={colStyles(col)} align={col.align || "right"}>
            {columnIndex === 0 ? (
              <Box alignItems={"center"} gap={"sm"}>
                <Icon name={isCollapsed ? "plus-square" : "minus-square"} />
                {value}
              </Box>
            ) : (
              value
            )}
          </td>
        );
      })}
      {hasRowActions && <td />}
    </tr>
  );
});

const colStyles = <T,>(col: TableColumn<T>): CSSProperties => ({
  width: col.compact ? "1px" : col.width,
  minWidth: col.compact ? "unset" : undefined,
});

const hasError = (value: TableColumnValue): boolean | undefined => {
  if (Array.isArray(value)) {
    switch (value[0]) {
      case "mdate-input":
      case "mtext-input":
        return !value[1].model.isValid;
    }
  }
};

const renderRowAction = (action: RowAction, i: number) => {
  switch (action[0]) {
    case "button":
      return <Button key={i} {...action[1]} />;
    case "icon":
      return <IconButton key={i} {...action[1]} />;
    case "link":
      return <LinkButton key={i} {...action[1]} />;
    case "context-menu": {
      const [, items, buttonProps] = action;
      return (
        <DropdownMenu
          key={i}
          button={{ type: "dotdotdot", ...buttonProps }}
          alignment={"end"}
          items={items}
        />
      );
    }
    case "react-element":
      return <Fragment key={i}>{action[1]}</Fragment>;
  }
};

const renderCellValue = ({
  value: val,
  isEditing,
  isEditable,
  isReadonly,
  align,
  variant,
  tf,
  onPopoverClose,
}: {
  value: TableColumnValue;
  isEditing: boolean;
  isEditable: boolean;
  isReadonly: boolean | undefined;
  align: TableColumn<unknown>["align"];
  variant: TableProps["variant"];
  tf: PreppedI18nState["tf"];
  onPopoverClose: () => void;
}) => {
  const renderText = (val: string | number | TMessage | TMessageParam) =>
    variant === "EDITABLE" ? (
      <div className={"px-md py-sm whitespace-nowrap"}>{tf(val)}</div>
    ) : (
      tf(val)
    );

  switch (true) {
    case Array.isArray(val):
      switch (val[0]) {
        case "checkbox":
          return (
            <div onClick={stopPropagation()}>
              <Checkbox {...val[1]} />
            </div>
          );
        case "icon":
          return (
            <VBox paddingX>
              <Icon {...val[1]} />
            </VBox>
          );
        case "icon-button":
          return (
            <div onClick={stopPropagation()}>
              <IconButton {...val[1]} />
            </div>
          );
        case "info":
          return (
            <div onClick={stopPropagation()}>
              <InfoButton {...val[1]} />
            </div>
          );
        case "link":
          return (
            <div onClick={stopPropagation()}>
              <LinkButton {...val[1]} />
            </div>
          );
        case "tag":
          return <Tag {...val[1]} />;
        case "mdate-input":
          if (isEditable && !isEditing) {
            return <DateInputView model={val[1].model} readonly={isReadonly} />;
          } else {
            return <MDateInput autoSelect={!isEditable} autofocus={isEditable} {...val[1]} />;
          }
        case "mtext-input":
          if (isEditable && !isEditing) {
            return (
              <TextInputView
                model={val[1].model}
                align={align}
                displayValue={val[2]?.displayValue}
              />
            );
          } else {
            return (
              <MTextInput
                autoSelect={!isEditable}
                autofocus={isEditable}
                align={align}
                {...val[1]}
              />
            );
          }
        case "mtextarea":
          if (isEditable && !isEditing) {
            return <TextInputView model={val[1].model} align={align} />;
          } else {
            return <MTextArea autofocus={isEditable} {...val[1]} />;
          }
        case "select":
          if (isEditable && !isEditing) {
            return <SelectView {...val[1]} readonly={isReadonly} />;
          } else {
            return (
              <Select
                {...val[1]}
                autofocus={isEditable}
                onSelect={(item) => {
                  val[1].onSelect(item);
                }}
                onClose={onPopoverClose}
              />
            );
          }
        case "mselect":
          if (isEditable && !isEditing) {
            return <MSelectView {...val[1]} readonly={isReadonly} />;
          } else {
            return (
              <MSelect
                {...val[1]}
                autofocus={isEditable}
                onSelect={(item) => {
                  (val[1].onSelect ?? val[1].model.setValue)(item);
                }}
                onClose={onPopoverClose}
              />
            );
          }
        default:
          throw new Error("DataTable: unsupported column value type: " + val[0]);
      }
    case isTMessage(val):
    case isTMessageParam(val):
    case typeof val === "string" || typeof val === "number":
      return renderText(val);
    default:
      return val;
  }
};
